import { Col, Input, Row, Slider, Spin } from "antd";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toGetProducts } from "../../../apis/actions/service.actions";
import { toGetBrands, toGetModel } from "../../../apis/actions/vehicle.actions";
import { toAddOrRemoveProduct } from "../../../apis/slices/globalSlice";
import { AppDispatch, RootState } from "../../../apis/store";
import ListFetchLoader from "../../../Components/biblio/ListFetchLoader";
import PaginationComponent from "../../../Components/biblio/PaginationComponent";
import PageHeaderAllPage from "../../../Components/layouts/PageHeaderAllPage";
import ProductCategoryFilter from "../../../Components/shop/ProductCategoryFilter";
import { scroll } from "../../../utils/Utils";
import { signalCartAddedFeedback } from "../../../utils/cartAddedFeedback";
import Navbar from "../../Menu/Navbar";
import Footer from "../Sections/Footer";
import CardItem from "./CardItem";
import { getProductStockStatus } from "../../../utils/productStock";
import {
  shopGridContainerReduced,
  shopGridContainerVariants,
  shopGridItemReduced,
  shopGridItemVariants,
} from "./shopGridMotion";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import {
  markProductsFetched,
  shouldSkipProductsFetch,
} from "../../../utils/shopListCache";
import { consumeListScroll } from "../../../utils/shopListReturn";
import { useShopListQuery } from "../../../utils/shopListQuery";
import {
  resolveCategoryId,
  resolveListSort,
  SHOP_LIST_PAGE_SIZES,
} from "../../../utils/paginatedList";

const ShopList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { products } = useSelector((state: RootState) => state?.services);
  const {
    page: currentPage,
    limit: itemPerPage,
    search,
    sort: sortOption,
    categoryId,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setCategoryId,
  } = useShopListQuery();
  const [apply, setApply] = useState<boolean>(false);
  const [displaybleProducts, setDisplayableProducts] = useState<any>([]);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  const currentRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const sortLabels: Record<string, string> = {
    priceLowToHigh:
      i18n.language === "ar"
        ? "السعر : من الأقل إلى الأعلى"
        : "Price: Low to High",
    priceHighToLow:
      i18n.language === "ar"
        ? "السعر : من الأعلى إلى الأقل"
        : "Price: High to Low",
    nameAZ:
      i18n.language === "ar" ? "الاسم : من الألف إلى الياء" : "Name: A to Z",
    topRated: i18n.language === "ar" ? "الأكثر مبيعًا" : "Best Sellers",
  };

  useEffect(() => {
    dispatch(toGetBrands(i18n.language));
    dispatch(toGetModel());
  }, [dispatch, i18n.language]);

  useEffect(() => {
    const lang = i18n.language;
    if (
      shouldSkipProductsFetch(
        lang,
        currentPage,
        itemPerPage,
        categoryId,
        products?.status ?? "idle",
        sortOption
      )
    ) {
      return;
    }
    markProductsFetched(lang, currentPage, itemPerPage, categoryId, sortOption);
    dispatch(
      toGetProducts({
        lang,
        page: currentPage,
        limit: itemPerPage,
        categoryId,
        sort: sortOption,
      })
    );
    // Do not depend on products.status — rejected→retry loops forever as "loading"
  }, [dispatch, i18n.language, currentPage, itemPerPage, categoryId, sortOption]);

  useEffect(() => {
    const y = consumeListScroll("/products");
    if (y == null) return;
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const isProductsLoading =
    products?.status === "loading" ||
    (products?.request != null &&
      (products.request.page !== currentPage ||
        products.request.limit !== itemPerPage ||
        products.request.categoryId !== resolveCategoryId(categoryId) ||
        products.request.sort !== resolveListSort(sortOption))) ||
    (products?.status === "idle" &&
      (!Array.isArray(products?.list) || products.list.length === 0));
  const catalogTotal = Number(products?.total) || 0;

  useEffect(() => {
    toFilter(products?.list, apply, search);
  }, [products?.list, search, sortOption]);

  useEffect(() => {
    if (isProductsLoading || catalogTotal <= 0) return;
    const maxPage = Math.max(1, Math.ceil(catalogTotal / itemPerPage));
    if (currentPage > maxPage) setPage(maxPage);
  }, [isProductsLoading, catalogTotal, currentPage, itemPerPage, setPage]);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const maxPrice =
    products?.list?.length > 0
      ? Math.max(...products.list.map((item: any) => item.lst_price))
      : 0;
  const [range, setRange] = useState([0, 0]);

  useEffect(() => {
    setRange([0, maxPrice]);
  }, [maxPrice]);

  const handleRangeChange = (value: any) => {
    setRange(value);
  };

  const toFilter = (
    list: any,
    apply: boolean,
    search: string
  ) => {
    if (!Array.isArray(list)) {
      setDisplayableProducts([]);
      return;
    }

    let l: any = [...list];
    const [minPrice, maxPrice] = range;

    if (search !== "") {
      l = l.filter((el: any) =>
        el.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (apply && maxPrice !== -1 && maxPrice !== 0) {
      l = l.filter(
        (item: any) => item.lst_price >= minPrice && item.lst_price <= maxPrice
      );
    }

    // ponytail: sorting is server-side via `sort`; no client re-sort

    setDisplayableProducts(l);
  };

  return (
    <>
      <Navbar type="internal" />
      <main className="main">
        <PageHeaderAllPage
          tone="brand"
          showMenu
          current={t("menu.products")}
          banner="assets/imgs/page-header/products.jpg"
          link="products"
        />
        <section
          className="box-section block-content-tourlist background-body ac-page-shop-products"
          ref={currentRef}
        >
          <div className="container">
            <ProductCategoryFilter
              selectedId={categoryId}
              onSelect={setCategoryId}
            />
            <div className="box-content-main pt-20">
              <div className="content-right">
                <div className="box-filters mb-25 pb-5 border-bottom border-1">
                  <div className="row align-items-center">
                    <div className="col-xl-3 col-md-3 mb-10 text-lg-start text-center">
                      <div className="box-view-type">
                        <span className="text-sm-bold neutral-500 number-found d-inline-flex align-items-center gap-2 flex-wrap justify-content-center justify-content-lg-start">
                          {isProductsLoading ? (
                            <>
                              <Spin size="small" />
                              <span>{t("services.loadingData")}</span>
                            </>
                          ) : (
                            <>
                              {t("services.productItems", { count: catalogTotal })}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="col-xl-5 col-md-5 mb-10">
                      <div className="ac-shop-search-wrap">
                        <Input
                          allowClear
                          placeholder={t("services.search2")}
                          prefix={<SearchOutlined />}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="ac-search-input"
                          size="large"
                        />
                      </div>
                    </div>
                    <div className="col-xl-4 col-md-4 mb-10 text-lg-end text-center">
                      <div className="box-item-sort">
                        <span
                          className={`text-xs-medium neutral-500 ${
                            i18n.language === "ar" ? "ml-5" : "mr-5"
                          }`}
                        >
                          {t("services.sortBy")}
                        </span>
                        <div className="item-sort border-1">
                          <div className="dropdown dropdown-sort border-1-right">
                            <button
                              className="btn dropdown-toggle"
                              id="dropdownSort"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <span>{sortLabels[sortOption]}</span>
                            </button>
                            <ul
                              className="dropdown-menu dropdown-menu-light m-0"
                              aria-labelledby="dropdownSort"
                            >
                              {Object.keys(sortLabels).map((key) => (
                                <li className="hover-white" key={key}>
                                  <a
                                    className={`dropdown-item ${
                                      sortOption === key ? "active" : ""
                                    }`}
                                    href="#"
                                    onClick={() => setSort(key)}
                                  >
                                    {sortLabels[key]}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="box-grid-tours ac-shop-products">
                  {isProductsLoading ? (
                    <ListFetchLoader loading minHeight={400} />
                  ) : (
                    <motion.div
                      className="w-100"
                      key={`${currentPage}-${sortOption}-${i18n.language}-${displaybleProducts
                        .map((p: { id: number }) => p.id)
                        .join("-")}`}
                      variants={
                        prefersReducedMotion
                          ? shopGridContainerReduced
                          : shopGridContainerVariants
                      }
                      initial="hidden"
                      animate="visible"
                    >
                      <Row
                        className="ac-shop-product-grid"
                        gutter={isMobile ? [18, 28] : [32, 44]}
                        justify={isMobile ? "center" : undefined}
                      >
                        {displaybleProducts.map((el: any) => (
                            <Col
                              key={el.id}
                              xxl={6}
                              xl={6}
                              lg={8}
                              md={12}
                              sm={18}
                              xs={20}
                            >
                              <motion.div
                                className="h-100"
                                variants={
                                  prefersReducedMotion
                                    ? shopGridItemReduced
                                    : shopGridItemVariants
                                }
                              >
                                <CardItem
                                  type="product"
                                  description_website={el.description_website}
                                  id={el.id}
                                  name={el.name}
                                  description={el.description}
                                  img={el.image_1920}
                                  price={el.lst_price}
                                  discount={el.lst_price_discount}
                                  review={el.review}
                                  reviewCount={el.reviews_count}
                                  totalSalesCount={el.total_sales_count}
                                  stockAvailable={getProductStockStatus(el)}
                                  toAddProduct={() => {
                                    signalCartAddedFeedback();
                                    dispatch(toAddOrRemoveProduct(el));
                                  }}
                                />
                              </motion.div>
                            </Col>
                          ))}
                      </Row>
                    </motion.div>
                  )}
                </div>
                {!isProductsLoading && catalogTotal > 0 && (
                    <PaginationComponent
                      total={catalogTotal}
                      itemPerPage={itemPerPage}
                      pageSizeOptions={[...SHOP_LIST_PAGE_SIZES]}
                      active={currentPage}
                      setActive={(nb) => {
                        scroll(currentRef);
                        setPage(nb);
                      }}
                      onPageSizeChange={(size) => {
                        scroll(currentRef);
                        setLimit(size);
                      }}
                    />
                  )}
              </div>
              {/*
              <div className="content-left order-lg-first">
                <div className="sidebar-left ac-shop-sidebar">
                  <div className="box-filters-sidebar">
                    <div className="block-filter ac-shop-filter-panel">
                      <h6 className="text-lg-bold item-collapse neutral-1000">
                        {t("services.filter.price")}
                      </h6>
                      <div className="box-collapse scrollFilter">
                        <div className="pt-20">
                          <div className="box-slider-range">
                            <div>
                              <Slider
                                range
                                min={0}
                                max={maxPrice || 1}
                                value={range}
                                reverse={i18n.language == "ar"}
                                onChange={handleRangeChange}
                                tipFormatter={(value: any) => (
                                  <div className="ac--slider-tooltip">
                                    <span className="icon-saudi_riyal">
                                      {value}
                                    </span>
                                  </div>
                                )}
                                className="ac--slider-container-range"
                              />
                            </div>
                            <div className="box-value-price ac-shop-price-range-labels">
                              <span className="text-md-medium neutral-1000 icon-saudi_riyal">
                                {range[0]}
                              </span>
                              <span className="text-md-medium neutral-1000 icon-saudi_riyal">
                                {range[1]}
                              </span>
                            </div>
                            <input
                              className="value-money"
                              type="hidden"
                              value={range.join(",")}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="ac-shop-filter-actions">
                        <button
                          type="button"
                          className="ac-shop-filter-actions__clear"
                          onClick={() => {
                            setApply(false);
                            toFilter(
                              selectedCategories,
                              products?.list,
                              false,
                              search
                            );
                          }}
                        >
                          <MdFilterAltOff
                            aria-hidden
                            color="currentColor"
                            size={18}
                          />
                          {t("services.filter.all")}
                        </button>
                        <button
                          type="button"
                          className="ac-shop-filter-actions__apply"
                          onClick={() => {
                            setApply(true);
                            toFilter(
                              selectedCategories,
                              products?.list,
                              true,
                              search
                            );
                          }}
                        >
                          <MdFilterAlt aria-hidden color="currentColor" size={18} />
                          {t("profile.order.cart.apply")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sidebar-left ac-shop-sidebar">
                  <Brands />
                </div>
              </div>
              */}
            </div>
          </div>
        </section>
        <Footer type="external" />
      </main>
    </>
  );
};
export default ShopList;
